import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { GracePeriodService, GracePeriodStatus } from '../../services/grace-period.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-grace-period-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './grace-period-banner.component.html',
  styleUrls: ['./grace-period-banner.component.css']
})
export class GracePeriodBannerComponent implements OnInit, OnDestroy {
  private readonly gracePeriodService = inject(GracePeriodService);
  private readonly router = inject(Router);
  private subscription?: Subscription;

  gracePeriodStatus = signal<GracePeriodStatus | null>(null);
  isDismissed = signal<boolean>(false);

  showBanner = computed(() => {
    const status = this.gracePeriodStatus();
    return status?.isInGracePeriod && !this.isDismissed();
  });

  isUrgent = computed(() => {
    const status = this.gracePeriodStatus();
    return status?.daysRemaining === 1;
  });

  bannerColorClass = computed(() => {
    return this.isUrgent()
      ? 'bg-gradient-to-r from-red-600 to-red-500'
      : 'bg-gradient-to-r from-amber-500 to-orange-500';
  });

  ngOnInit(): void {
    // Check if banner was dismissed
    this.isDismissed.set(this.gracePeriodService.isBannerDismissed());

    // Subscribe to grace period status changes
    this.subscription = this.gracePeriodService.gracePeriodStatus$.subscribe(status => {
      this.gracePeriodStatus.set(status);

      // If no longer in grace period, clear dismissal
      if (status && !status.isInGracePeriod) {
        this.gracePeriodService.clearDismissal();
        this.isDismissed.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onDismiss(): void {
    this.gracePeriodService.dismissBanner();
    this.isDismissed.set(true);
  }

  onPayNow(): void {
    const unpaidCount = this.gracePeriodStatus()?.unpaidProfessionalsCount ?? 1;
    this.router.navigate(['/settings/professionals'], { queryParams: { payUnpaid: unpaidCount.toString() } });
  }

  getFormattedDate(): string {
    const endDate = this.gracePeriodStatus()?.endDate;
    if (!endDate) return '';

    const date = new Date(endDate);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  }
}

